import {useEffect, useState} from 'react';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import HeroSection from '../landing/HeroSection';
import WhyPerfectSection from '../landing/WhyPerfectSection';
import FiveReasonsSection from '../landing/FiveReasonsSection';
import CallMainCard from '../zvonki/CallMainCard';
import ZvonkiPaywall from '../paywall/ZvonkiPaywall';
import {useAccountStatus} from '../../hooks/useAccountStatus';
import {getPlans} from '../../api/plans';
import {ZVONKI_CONFIG} from '../../config/pageConfigs';
import {findRuTimezone} from '../../config/timezones';
import {blogArticles} from '../../config/blogArticles';
import {trackEvent} from '../../utils/analytics';

// Фолбэк на случай, пока тариф звонков не заведён в БД (или API недоступен):
// страница остаётся рабочей до пейвола, цена совпадает с прод-сидом.
const FALLBACK_PLANS = {
    subscriptions: [],
    topups: [
        {id: 'zvonok_1', name: '1 звонок', calls: 1, price: 390, oldPrice: null, discount: null, kind: 'topup'},
    ],
};

// Время звонка в выбранном при заказе поясе (не в поясе браузера) + город
// пояса, чтобы «12:00» читалось однозначно.
function formatCallDateTime(iso, timeZoneId) {
    const opts = {day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'};
    let formatted;
    try {
        formatted = new Date(iso).toLocaleString('ru-RU', {...opts, timeZone: timeZoneId || undefined});
    } catch {
        try {
            formatted = new Date(iso).toLocaleString('ru-RU', opts);
        } catch {
            return iso;
        }
    }
    const city = findRuTimezone(timeZoneId)?.city;
    return city ? `${formatted} (${city})` : formatted;
}

// Лендинг + приложение раздела звонков (/zvonki). Зеркалит главный лендинг, но
// вместо шаблонов — звезды, вместо картинки-результата — запланированный
// звонок, свой пейвол с тарифами product=zvonki.
export default function ZvonkiPage() {
    const {zvonokCredits, refetch: fetchAccountStatus} = useAccountStatus();
    const [plans, setPlans] = useState({subscriptions: [], topups: []});
    const [showPaywall, setShowPaywall] = useState(false);
    // Заполненная заявка на звонок, с которой открылся пейвол: уедет вместе с
    // покупкой, и вебхук запланирует звонок сразу после оплаты.
    const [pendingCall, setPendingCall] = useState(null);
    const [showPostPurchase, setShowPostPurchase] = useState(false);
    const [scheduledCall, setScheduledCall] = useState(null);

    // Set meta tags
    useEffect(() => {
        document.title = ZVONKI_CONFIG.title;
        document.querySelector('meta[name="description"]')?.setAttribute('content', ZVONKI_CONFIG.description);
    }, []);

    // Тарифы звонков (product=zvonki, независимы от видео) запрашиваем при
    // открытии пейвола, а не на каждом заходе на лендинг. Ретраи — внутри
    // getPlans (apiGetWithRetry); если все попытки упали — FALLBACK_PLANS.
    useEffect(() => {
        if (!showPaywall) return;
        getPlans('zvonki').then(data => {
            const mapPlan = (p) => ({
                id: p.id,
                name: p.name,
                calls: p.zvonok_credits,
                price: p.price_rub,
                oldPrice: p.old_price_rub,
                discount: p.discount_percent || null,
                kind: p.kind || 'topup',
            });
            const topups = (data.topups || []).map(mapPlan).filter(p => p.calls > 0);
            setPlans(topups.length > 0 ? {subscriptions: [], topups} : FALLBACK_PLANS);
        }).catch(() => setPlans(FALLBACK_PLANS));
    }, [showPaywall]);

    const handleScheduled = (call) => {
        if (call) {
            setScheduledCall(call);
        }
    };

    const handleNoCredits = (persona, callPayload) => {
        trackEvent('see_paywall_zvonki');
        setPendingCall(callPayload ? {...callPayload, persona} : null);
        setShowPaywall(true);
    };

    const handlePurchaseSuccess = () => {
        fetchAccountStatus();
        setShowPaywall(false);
        if (pendingCall) {
            // Вебхук оплаты запланирует звонок сам — сразу показываем экран
            // «Звонок запланирован» с данными заявки.
            setScheduledCall({
                persona: pendingCall.persona,
                phone: pendingCall.phone,
                scheduledAt: pendingCall.scheduledAt,
                timezone: pendingCall.timezone,
                autoScheduled: true,
            });
            setPendingCall(null);
        } else {
            setShowPostPurchase(true);
        }
    };

    // Scheduled Call Result State
    if (scheduledCall) {
        return (
            <div className="min-h-screen bg-white font-sans flex items-center justify-center p-4">
                <div className="max-w-xl w-full text-center">
                    <div
                        className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-center">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
                             strokeLinecap="round" strokeLinejoin="round">
                            <path
                                d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                    </div>
                    <h2 className="text-[42px] font-bold text-brand-dark mb-4 leading-[1.1] tracking-[-0.42px]">
                        Звонок запланирован!
                    </h2>
                    {scheduledCall.autoScheduled && (
                        <p className="text-base text-green-600 font-medium mb-2">
                            Оплата прошла — звонок уже в работе
                        </p>
                    )}
                    {scheduledCall.persona && (
                        <p className="text-lg text-brand-dark/70 mb-2">
                            {scheduledCall.persona.name} позвонит на {scheduledCall.phone}
                        </p>
                    )}
                    <p className="text-lg font-semibold text-brand-dark mb-8">
                        {formatCallDateTime(scheduledCall.scheduledAt, scheduledCall.timezone)}
                    </p>
                    <p className="text-sm text-brand-dark/50 mb-8 leading-relaxed">
                        Если абонент не возьмёт трубку, мы перезвоним ещё два раза с интервалом
                        в несколько минут. Не дозвонимся — вернём кредит. Статус звонка — в разделе{' '}
                        <a href="/orders" className="underline hover:text-brand-red">Мои заказы</a>.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <button
                            onClick={() => setScheduledCall(null)}
                            className="px-8 py-4 bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white font-medium rounded-2xl hover:opacity-90 transition shadow-lg"
                        >
                            Заказать ещё звонок
                        </button>
                        <a
                            href="/orders"
                            className="px-8 py-4 bg-brand-gray text-brand-dark font-medium rounded-2xl hover:bg-gray-200 transition"
                        >
                            Мои заказы
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white font-sans overflow-x-hidden relative">

            <div className="relative z-10">
                <Header/>

                <main className="pt-6">
                    <HeroSection config={ZVONKI_CONFIG.hero}/>

                    <CallMainCard
                        credits={zvonokCredits}
                        onScheduled={handleScheduled}
                        onAccountStatusUpdate={fetchAccountStatus}
                        onNoCredits={handleNoCredits}
                    />

                    <WhyPerfectSection config={ZVONKI_CONFIG.whyPerfect}/>

                    <FiveReasonsSection reasons={ZVONKI_CONFIG.fiveReasons}/>

                    {/* Blog articles */}
                    <div className="max-w-[1110px] mx-auto px-4 pb-10 md:pb-16">
                        <h2 className="text-[28px] md:text-[42px] font-bold text-brand-dark leading-[1.1] tracking-[-0.42px] mb-8 text-center">
                            Полезные{' '}
                            <span className="text-gradient">статьи</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {blogArticles.slice(0, 4).map(article => (
                                <a
                                    key={article.slug}
                                    href={`/blog/${article.slug}/`}
                                    className="group block rounded-2xl border border-brand-dark/10 p-5 hover:border-brand-red/30 hover:shadow-md transition"
                                >
                                    <h3 className="font-bold text-brand-dark group-hover:text-brand-red transition leading-snug">
                                        {article.title}
                                    </h3>
                                    <p className="text-sm text-brand-dark/60 mt-2 leading-relaxed">{article.excerpt}</p>
                                </a>
                            ))}
                        </div>
                        <div className="text-center mt-6">
                            <a
                                href="/blog/"
                                className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-medium hover:opacity-95 transition"
                            >
                                Все статьи блога
                            </a>
                        </div>
                    </div>
                </main>

                <Footer/>
            </div>

            {/* Post-Purchase Suggestion Modal */}
            {showPostPurchase && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-[24px] p-8 max-w-md w-full shadow-2xl text-center">
                        <div
                            className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-center">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"
                                 strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 6L9 17l-5-5"/>
                            </svg>
                        </div>

                        <h3 className="text-2xl font-bold text-brand-dark mb-2">
                            Оплата прошла!
                        </h3>
                        <p className="text-brand-dark/60 mb-6">
                            Кредиты зачислены на ваш баланс.
                            Запланируйте звонок прямо сейчас!
                        </p>

                        <button
                            onClick={() => {
                                setShowPostPurchase(false);
                                window.scrollTo({top: 0, behavior: 'smooth'});
                            }}
                            className="w-full py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-base font-semibold hover:opacity-95 transition"
                        >
                            Запланировать звонок
                        </button>
                    </div>
                </div>
            )}

            <ZvonkiPaywall
                isOpen={showPaywall}
                onClose={() => setShowPaywall(false)}
                plans={plans}
                pendingCall={pendingCall}
                onPurchaseSuccess={handlePurchaseSuccess}
            />
        </div>
    );
}
